import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowRight, Check, Loader2, MessageCircle, Sparkles } from 'lucide-react';
import { publicLeadLinkApi } from '../../services/vendorExternalLeadService';
import MarketingReveal from '../../components/home/MarketingReveal';
import DynamicQuestionFields from '../../components/shared/DynamicQuestionFields';
import { DEFAULT_EXTERNAL_FORM_FIELDS } from '../../utils/leadFormFields';

const FALLBACK_HERO =
  'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80';

const locationCredit = (vendor) => {
  const parts = [vendor?.city, vendor?.country].filter(Boolean);
  return parts.join(' — ');
};

const HeroMedia = ({ src, brandColor }) => (
  <div className="absolute inset-0 overflow-hidden">
    <img
      src={src}
      alt=""
      className="h-full w-full scale-105 object-cover"
      style={{ animation: 'leadHeroDrift 18s ease-in-out infinite alternate' }}
    />
    <div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 45%, rgba(10,10,10,0.92) 100%), linear-gradient(105deg, ${brandColor}55 0%, transparent 55%)`,
      }}
    />
  </div>
);

const ExternalLeadForm = () => {
  const { token, vendorSlug } = useParams();
  // Support /lead/{token} and branded /lead/{vendor-name}/{token}
  const leadToken = vendorSlug && token ? token : token || vendorSlug;
  const [submitted, setSubmitted] = useState(false);
  const [thankYou, setThankYou] = useState('');
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['publicLeadLink', leadToken],
    queryFn: () => publicLeadLinkApi.get(leadToken),
    enabled: Boolean(leadToken),
    retry: false,
  });

  const link = data?.data;
  const fields = useMemo(() => {
    return link?.form_fields?.length ? link.form_fields : DEFAULT_EXTERNAL_FORM_FIELDS;
  }, [link?.form_fields]);

  const submitMutation = useMutation({
    mutationFn: (payload) => publicLeadLinkApi.submit(leadToken, payload),
    onSuccess: (res) => {
      setThankYou(res?.message || link?.thank_you_message || 'Thanks — we received your inquiry.');
      setSubmitted(true);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    onError: (err) => {
      setErrors(err?.response?.data?.errors || {});
    },
  });

  const onFieldChange = (id, value) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    submitMutation.mutate({ answers: values, ...values });
  };

  if (isLoading) {
    return (
      <div className="theme-marketing marketing-app flex min-h-[100dvh] items-center justify-center bg-[#111]">
        <Loader2 className="h-8 w-8 animate-spin text-white/80" />
      </div>
    );
  }

  if (error || !link) {
    return (
      <div className="theme-marketing marketing-app flex min-h-[100dvh] items-center justify-center bg-[#111] px-4 py-16 text-center">
        <div className="w-full max-w-md">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-white/45">Inquiry</p>
          <h1 className="marketing-section-title mb-3 text-white">Link unavailable</h1>
          <p className="text-base leading-relaxed text-white/65">
            This inquiry form is inactive or the link is incorrect.
          </p>
        </div>
      </div>
    );
  }

  const brandColor = link.vendor?.brand_color || '#111111';
  const brandName = link.vendor?.business_name || 'Inquiry';
  const credit = locationCredit(link.vendor);
  const heroSrc = link.vendor?.cover_url || link.vendor?.logo_url || FALLBACK_HERO;

  if (submitted) {
    const nextSteps = [
      {
        icon: Check,
        title: 'Inquiry received',
        body: thankYou || 'We’ve got your details and will review them shortly.',
      },
      {
        icon: MessageCircle,
        title: `${brandName} will reach out`,
        body: 'Expect a personal reply to confirm availability and next steps.',
      },
      {
        icon: Sparkles,
        title: 'Plan the details together',
        body: 'Share more about your vision once you’re connected.',
      },
    ];

    return (
      <div className="theme-marketing marketing-app min-h-[100dvh] bg-[#111] text-white">
        <style>{`
          @keyframes leadHeroDrift { from { transform: scale(1.05); } to { transform: scale(1.12); } }
          @keyframes leadCheckIn { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes leadFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>

        <div className="relative min-h-[42vh] sm:min-h-[48vh]">
          <HeroMedia src={heroSrc} brandColor={brandColor} />
          <div className="relative flex min-h-[42vh] flex-col justify-end px-4 pb-10 pt-16 sm:min-h-[48vh] sm:px-8 sm:pb-14">
            <MarketingReveal className="mx-auto w-full max-w-3xl">
              <div
                className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#111] shadow-lg sm:h-16 sm:w-16"
                style={{ animation: 'leadCheckIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both' }}
              >
                <Check className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.5} />
              </div>
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-white/55">
                {brandName}
              </p>
              <h1
                className="marketing-hero-title mb-3 max-w-xl text-[2.25rem] text-white sm:text-[clamp(2.5rem,5vw,3.5rem)]"
                style={{ animation: 'leadFadeUp 0.6s ease both 0.1s' }}
              >
                You’re all set
              </h1>
              <p
                className="max-w-lg text-[1.05rem] leading-relaxed text-white/75"
                style={{ animation: 'leadFadeUp 0.6s ease both 0.2s' }}
              >
                {thankYou}
              </p>
              {credit ? (
                <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.14em] text-white/40">
                  {credit}
                </p>
              ) : null}
            </MarketingReveal>
          </div>
        </div>

        <section className="border-t border-white/10 bg-[#111] px-4 py-10 sm:px-8 sm:py-14">
          <MarketingReveal delayMs={80} className="mx-auto w-full max-w-3xl">
            <p className="mb-6 text-[11px] font-medium uppercase tracking-[0.18em] text-white/45">
              What happens next
            </p>
            <ol className="divide-y divide-white/10 border-y border-white/10">
              {nextSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <li
                    key={step.title}
                    className="flex gap-4 py-5 sm:gap-5 sm:py-6"
                    style={{ animation: `leadFadeUp 0.55s ease both ${0.15 + i * 0.08}s` }}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white">
                      <Icon className="h-[18px] w-[18px]" />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <p className="text-[0.95rem] font-semibold text-white sm:text-base">{step.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-white/60 sm:text-[0.95rem]">{step.body}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
            {link.vendor?.logo_url ? (
              <div className="mt-10 flex items-center gap-3">
                <img
                  src={link.vendor.logo_url}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20"
                />
                <div>
                  <p className="text-sm font-semibold text-white">{brandName}</p>
                  {credit ? <p className="text-xs text-white/45">{credit}</p> : null}
                </div>
              </div>
            ) : (
              <p className="mt-10 text-lg font-semibold tracking-tight text-white">{brandName}</p>
            )}
          </MarketingReveal>
        </section>
      </div>
    );
  }

  return (
    <div className="theme-marketing marketing-app min-h-[100dvh] overflow-x-hidden bg-[#111]">
      <style>{`
        @keyframes leadHeroDrift { from { transform: scale(1.05); } to { transform: scale(1.12); } }
        @keyframes leadFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Peerspace-style: cinematic hero + dark copy band */}
      <header className="relative">
        <div className="relative min-h-[38vh] sm:min-h-[44vh]">
          <HeroMedia src={heroSrc} brandColor={brandColor} />
          <div className="relative flex min-h-[38vh] flex-col justify-end px-4 pb-8 pt-14 sm:min-h-[44vh] sm:px-8 sm:pb-12">
            <MarketingReveal className="mx-auto w-full max-w-3xl text-white">
              <div className="mb-5 flex items-center gap-3">
                {link.vendor?.logo_url ? (
                  <img
                    src={link.vendor.logo_url}
                    alt=""
                    className="h-11 w-11 rounded-full object-cover ring-2 ring-white/30 sm:h-12 sm:w-12"
                  />
                ) : null}
                <div className="min-w-0">
                  <h1
                    className="truncate text-[1.35rem] font-bold tracking-tight text-white sm:text-[1.65rem]"
                    style={{ animation: 'leadFadeUp 0.55s ease both' }}
                  >
                    {brandName}
                  </h1>
                  {credit ? (
                    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/50">
                      {credit}
                    </p>
                  ) : (
                    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/50">
                      Private inquiry
                    </p>
                  )}
                </div>
              </div>
              <h2
                className="marketing-hero-title mb-3 max-w-xl text-[1.85rem] text-white sm:text-[clamp(2.1rem,4.2vw,3rem)]"
                style={{ animation: 'leadFadeUp 0.55s ease both 0.08s' }}
              >
                {link.headline || 'Tell us about your event'}
              </h2>
              <p
                className="max-w-md text-[0.98rem] leading-relaxed text-white/72 sm:text-[1.05rem]"
                style={{ animation: 'leadFadeUp 0.55s ease both 0.16s' }}
              >
                {link.welcome_message || 'Share a few details and we’ll get back to you shortly.'}
              </p>
            </MarketingReveal>
          </div>
        </div>
      </header>

      {/* Interactive form — floating card at the photo → dark seam */}
      <section className="relative z-10 -mt-6 px-3 pb-[max(2rem,env(safe-area-inset-bottom))] sm:-mt-10 sm:px-6 sm:pb-16">
        <MarketingReveal
          delayMs={60}
          className="mx-auto w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-[0_28px_80px_-28px_rgba(0,0,0,0.55)] ring-1 ring-black/5"
        >
          <div className="border-b border-black/[0.06] px-4 py-4 sm:px-8 sm:py-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6b6b6b]">
              Request details
            </p>
            <p className="mt-1 text-sm text-[#6b6b6b]">
              Takes about a minute — no account needed.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5 px-4 py-5 sm:space-y-6 sm:px-8 sm:py-8">
            <DynamicQuestionFields
              fields={fields}
              values={values}
              onChange={onFieldChange}
              errors={errors}
              variant="marketing"
            />

            {submitMutation.isError && !Object.keys(errors).length ? (
              <p className="text-sm text-red-600">
                {submitMutation.error?.response?.data?.message || 'Could not send. Try again.'}
              </p>
            ) : null}

            <div className="sticky bottom-0 -mx-4 border-t border-black/[0.06] bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:pt-1 sm:backdrop-blur-none">
              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="group flex w-full touch-manipulation items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-[0.95rem] font-semibold text-white transition-[transform,background-color,opacity] duration-150 hover:brightness-110 active:scale-[0.99] disabled:opacity-60 sm:rounded-[14px] sm:py-4"
                style={{ backgroundColor: brandColor === '#0a0a0a' ? '#111111' : brandColor }}
              >
                {submitMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send inquiry
                    <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
              <p className="mt-3 text-center text-xs text-[#8a8a8a]">
                Sent directly to {brandName}
              </p>
            </div>
          </form>
        </MarketingReveal>
      </section>
    </div>
  );
};

export default ExternalLeadForm;
